/**
 * Results component for the asset metadata upload process
 */
import { SparseAsset } from '@/asset/api/types';
import { AssetMetadataUploadResult } from '@/asset/api/upload-asset-metadata';
import type { ExpandedAssetsState } from '@/asset/components/asset-metadata-upload/types';
import { Link } from '@/components/link';
import {
  CheckCircle as CheckCircleIcon,
  CheckRounded,
  ContentCopyRounded,
  Error as ErrorIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
} from '@/constants/hardfin-standard-icons';
import { ItemDetail } from '@/items/api/types';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import {
  createIdentifierSuffix,
  formatFailedRowsForCopy,
} from '@/utilities/upload-helpers';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  DialogActions,
  IconButton,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { FixedSizeList as VirtualList } from 'react-window';

const ITEM_HEIGHT = 40;
const FAILED_ROW_HEIGHT = 80;

interface ResultsProps {
  selectedItem: ItemDetail;
  uploadResult: AssetMetadataUploadResult | null;
  expandedAssets: ExpandedAssetsState;
  onToggleExpanded: (section: keyof ExpandedAssetsState) => void;
  onBack: () => void;
  onStartNew: () => void;
  onClose: () => void;
}

interface RowFailure {
  rowNumber: number;
  serial: string;
  errors: string[];
}

interface AssetRowProps {
  index: number;
  style: React.CSSProperties;
  data: SparseAsset[];
}

const AssetRow = ({ index, style, data }: AssetRowProps) => {
  const { serial, key } = data[index];
  return (
    <div style={style}>
      <ListItem component="div" dense>
        <ListItemText
          primary={
            <Link to={`/assets/$assetKey`} params={{ assetKey: key }}>
              {serial}
            </Link>
          }
        />
      </ListItem>
    </div>
  );
};

interface FailedRowItemProps {
  index: number;
  style: React.CSSProperties;
  data: RowFailure[];
}

const FailedRowItem = ({ index, style, data }: FailedRowItemProps) => {
  const row = data[index];
  return (
    <div style={style}>
      <ListItem component="div" dense>
        <ListItemText
          primary={`For file row ${row.rowNumber} (serial "${row.serial || 'Missing Serial'}"):`}
          secondary={
            <Stack spacing={1} mt={1}>
              {row.errors.map((error: string, idx: number) => (
                <Typography key={idx} variant="body2" color="error">
                  {error}
                </Typography>
              ))}
            </Stack>
          }
        />
      </ListItem>
    </div>
  );
};

/**
 * Renders the results step of the asset metadata upload process
 */
export function Results({
  selectedItem,
  uploadResult,
  expandedAssets,
  onToggleExpanded,
  onBack,
  onStartNew,
  onClose,
}: ResultsProps) {
  const [isCopied, setIsCopied] = useState(false);

  if (!uploadResult) {
    return (
      <Card>
        <CardContent>
          <Typography color="error">
            No upload results available. Please try again.
          </Typography>
          <Button variant="contained" onClick={onStartNew} sx={{ mt: 2 }}>
            Start New Upload
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { success, items, structureErrors } = uploadResult;

  const assetsUpdated: SparseAsset[] = [];
  const assetsNotUpdated: SparseAsset[] = [];
  const failedRows: RowFailure[] = [];

  /**
   * Handles copying all failed row errors to clipboard with visual feedback
   */
  const handleCopyErrors = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion toggle when clicking copy
    try {
      const text = formatFailedRowsForCopy(failedRows, (row) =>
        createIdentifierSuffix(row, 'serial', 'serial')
      );
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy errors:', error);
    }
  };

  items.forEach((item) => {
    if (item.errors.length > 0) {
      failedRows.push({
        rowNumber: item.index,
        serial: item.serial,
        // Field <field name> is a <field type>, but "<value>" was uploaded
        errors: item.errors.map(
          (err) => `Column "${err.fieldName}" has error: ${err.error}`
        ),
      });
      return;
    }
    if (item.fieldsUpdated) {
      if (Object.values(item.fieldsUpdated).some((updated) => updated)) {
        assetsUpdated.push(item.validatedAsset as SparseAsset);
      } else {
        assetsNotUpdated.push(item.validatedAsset as SparseAsset);
      }
    } else {
      assetsNotUpdated.push(item.validatedAsset as SparseAsset);
    }
  });

  return (
    <Card>
      <CardContent>
        {success ? (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              <AlertTitle>Success</AlertTitle>
              <Typography variant="body2">
                Your asset metadata uploaded successfully for{' '}
                {pluralize(assetsUpdated.length + assetsNotUpdated.length, {
                  singular: 'asset',
                  plural: 'assets',
                })}
              </Typography>
            </Alert>

            <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: 'success.light',
                  color: 'success.contrastText',
                  cursor: assetsUpdated.length ? 'pointer' : undefined,
                }}
                onClick={
                  assetsUpdated.length
                    ? () => onToggleExpanded('updated')
                    : undefined
                }
              >
                <Box display="flex" alignItems="center">
                  <CheckCircleIcon sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">
                    Assets with updates ({formatNumber(assetsUpdated.length)})
                  </Typography>
                </Box>
                {assetsUpdated.length > 0 &&
                  (expandedAssets.updated ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  ))}
              </Box>
              <Collapse in={expandedAssets.updated}>
                <VirtualList
                  height={Math.min(assetsUpdated.length * ITEM_HEIGHT, 200)}
                  itemCount={assetsUpdated.length}
                  itemSize={ITEM_HEIGHT}
                  itemData={assetsUpdated}
                  width="100%"
                >
                  {AssetRow}
                </VirtualList>
              </Collapse>
            </Paper>

            <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: (theme) => theme.palette.grey.A400,
                  color: 'info.contrastText',
                  cursor: assetsNotUpdated.length ? 'pointer' : undefined,
                }}
                onClick={
                  assetsNotUpdated.length
                    ? () => onToggleExpanded('notUpdated')
                    : undefined
                }
              >
                <Box display="flex" alignItems="center">
                  <InfoIcon sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">
                    Assets with no updates (
                    {formatNumber(assetsNotUpdated.length)})
                  </Typography>
                </Box>
                {assetsNotUpdated.length > 0 &&
                  (expandedAssets.notUpdated ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  ))}
              </Box>
              <Collapse in={expandedAssets.notUpdated}>
                <VirtualList
                  height={Math.min(assetsNotUpdated.length * ITEM_HEIGHT, 200)}
                  itemCount={assetsNotUpdated.length}
                  itemSize={ITEM_HEIGHT}
                  itemData={assetsNotUpdated}
                  width="100%"
                >
                  {AssetRow}
                </VirtualList>
              </Collapse>
            </Paper>
          </>
        ) : (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {structureErrors.length > 0 && (
                <>
                  <AlertTitle>File format invalid</AlertTitle>
                  <Typography variant="body2">
                    The uploaded file has column names that do not match your
                    item fields
                  </Typography>
                </>
              )}
              {failedRows.length > 0 && (
                <>
                  <AlertTitle>Uploaded data invalid</AlertTitle>
                  <Typography variant="body2">
                    The uploaded file includes data that does not match your
                    assets or item fields
                  </Typography>
                </>
              )}
            </Alert>
            {structureErrors && structureErrors.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => onToggleExpanded('structureErrors')}
                >
                  <Box display="flex" alignItems="center">
                    <ErrorIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Mismatched columns for {selectedItem.name} (
                      {formatNumber(structureErrors.length)})
                    </Typography>
                  </Box>
                  {expandedAssets.structureErrors ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </Box>
                <Collapse in={expandedAssets.structureErrors}>
                  <ul
                    style={{
                      maxHeight: '300px',
                      overflow: 'auto',
                      listStyleType: 'disc',
                    }}
                  >
                    {structureErrors.map((row) => (
                      <li
                        key={`structure-${row.fieldName}`}
                        style={{ paddingBottom: '8px' }}
                      >
                        Column "{`${row.fieldName}`}" not found
                      </li>
                    ))}
                  </ul>
                </Collapse>
              </Paper>
            )}
            {failedRows && failedRows.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => onToggleExpanded('failed')}
                >
                  <Box display="flex" alignItems="center">
                    <ErrorIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Invalid rows ({formatNumber(failedRows.length)}{' '}
                      {pluralize(failedRows.length, {
                        includeCount: false,
                        singular: 'asset',
                        plural: 'assets',
                      })}
                      )
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip
                      title={isCopied ? 'Copied!' : 'Copy all errors'}
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={handleCopyErrors}
                        aria-label={isCopied ? 'Copied' : 'Copy all errors'}
                        sx={{
                          color: 'error.contrastText',
                          '&:hover': {
                            bgcolor: 'error.dark',
                          },
                        }}
                      >
                        {isCopied ? (
                          <CheckRounded fontSize="small" />
                        ) : (
                          <ContentCopyRounded fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    {expandedAssets.failed ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </Box>
                </Box>
                <Collapse in={expandedAssets.failed}>
                  <VirtualList
                    height={Math.min(
                      failedRows.length * FAILED_ROW_HEIGHT,
                      300
                    )}
                    itemCount={failedRows.length}
                    itemSize={FAILED_ROW_HEIGHT}
                    itemData={failedRows}
                    width="100%"
                  >
                    {FailedRowItem}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}
          </>
        )}

        <Box display="flex" justifyContent="space-between">
          <DialogActions>
            <Button variant="outlined" onClick={onBack}>
              Back
            </Button>
          </DialogActions>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
            <Button variant="contained" onClick={onStartNew}>
              Start new upload
            </Button>
          </DialogActions>
        </Box>
      </CardContent>
    </Card>
  );
}
